import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Checkbox,
  Chip,
  Switch,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSection,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyForm = {
  code: '',
  name: '',
  bonus_points: '',
  expiry_days: '',
  inventory_ids: [],
  is_active: true,
  valid_from: '',
  valid_until: '',
  usage_limit: '',
  per_user_limit: '1',
  audience: 'both',
};

const AUDIENCE_OPTIONS = [
  { value: 'both', label: 'New sign-ups and existing customers' },
  { value: 'signup', label: 'New sign-ups only (Create Account)' },
  { value: 'existing', label: 'Existing customers only (Wallet Bonus)' },
];

const STATUS_CHIPS = {
  active: { label: 'Active', bg: '#E3F4EA', fg: '#1E6B3A' },
  scheduled: { label: 'Scheduled', bg: '#E3EEFA', fg: '#0B4F8A' },
  limit_reached: { label: 'Limit reached', bg: '#FDEBDD', fg: '#9A3412' },
  expired: { label: 'Expired', bg: '#F1EDF2', fg: '#4A4550' },
  inactive: { label: 'Inactive', bg: '#F1EDF2', fg: '#4A4550' },
};

// ISO (UTC) from the API -> value for a datetime-local input, in local time.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

const firstError = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (data.detail || data.message) return data.detail || data.message;
  const key = Object.keys(data)[0];
  if (!key) return null;
  const val = Array.isArray(data[key]) ? data[key][0] : data[key];
  return key === 'non_field_errors' ? val : `${key.replace(/_/g, ' ')}: ${val}`;
};

/**
 * Special offer codes: bonus wallet points for a code entered on Create Account
 * (credited once the OTP is verified) and/or on the app's Wallet Bonus screen,
 * per `audience`. Per-user limit caps uses of each code by one customer; hubs
 * empty = all.
 */
export function SpecialOfferCodes() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const hubOptions = useMemo(
    () =>
      inventoryList.map((h) => ({
        value: Number(h.value ?? h.id),
        name: h.label || h.place || h.name || `Hub #${h.value ?? h.id}`,
      })),
    [inventoryList]
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [redemptionsFor, setRedemptionsFor] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const res = await CustomAxios.get('special-offer-codes/', { params });
      setCodes(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Failed to load offer codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(updatepageName('special_offer_codes'));
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(fetchCodes, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const hubsLabel = (row) => {
    const places = row.inventory_places || [];
    if (!places.length) return 'All hubs';
    return places.length <= 3 ? places.join(', ') : `${places.slice(0, 3).join(', ')} (+${places.length - 3})`;
  };

  const periodLabel = (row) => {
    const from = formatDate(row.valid_from);
    const until = formatDate(row.valid_until);
    if (from && until) return `${from} – ${until}`;
    if (from) return `From ${from} (no end)`;
    if (until) return `Until ${until}`;
    return 'Always';
  };

  const openCreate = () => {
    setEditRow(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      code: row.code || '',
      name: row.name || '',
      bonus_points: String(row.bonus_points ?? ''),
      expiry_days: row.expiry_days != null ? String(row.expiry_days) : '',
      inventory_ids: (row.inventory_ids || []).map(Number),
      is_active: Boolean(row.is_active),
      valid_from: toLocalInput(row.valid_from),
      valid_until: toLocalInput(row.valid_until),
      usage_limit: row.usage_limit != null ? String(row.usage_limit) : '',
      per_user_limit: row.per_user_limit != null ? String(row.per_user_limit) : '',
      audience: row.audience || 'both',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const code = form.code.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,20}$/.test(code)) {
      showToast('warn', 'Validation', 'Code must be 4–20 letters or digits, no spaces');
      return;
    }
    if (!(Number(form.bonus_points) > 0)) {
      showToast('warn', 'Validation', 'Bonus points must be greater than 0');
      return;
    }
    if (form.valid_from && form.valid_until && new Date(form.valid_until) <= new Date(form.valid_from)) {
      showToast('warn', 'Validation', 'Valid until must be after valid from');
      return;
    }

    if (form.per_user_limit !== '' && !(Number(form.per_user_limit) >= 1)) {
      showToast('warn', 'Validation', 'Per-user limit must be at least 1, or blank for unlimited');
      return;
    }

    const payload = {
      code,
      name: form.name.trim(),
      bonus_points: Number(form.bonus_points),
      expiry_days: form.expiry_days === '' ? null : Number(form.expiry_days),
      inventory_ids: (form.inventory_ids || []).map(Number),
      is_active: Boolean(form.is_active),
      valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
      valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      usage_limit: form.usage_limit === '' ? null : Number(form.usage_limit),
      per_user_limit: form.per_user_limit === '' ? null : Number(form.per_user_limit),
      audience: form.audience || 'both',
    };

    try {
      setSaving(true);
      if (editRow) {
        await CustomAxios.patch(`special-offer-codes/${editRow.id}/`, payload);
        showToast('success', 'Saved', 'Offer code updated');
      } else {
        await CustomAxios.post('special-offer-codes/', payload);
        showToast('success', 'Created', 'Offer code created');
      }
      setModalOpen(false);
      fetchCodes();
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await CustomAxios.patch(`special-offer-codes/${row.id}/`, { is_active: !row.is_active });
      fetchCodes();
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Update failed');
    }
  };

  const openRedemptions = async (row) => {
    setRedemptionsFor(row);
    setRedemptions([]);
    try {
      setRedemptionsLoading(true);
      const res = await CustomAxios.get(`special-offer-codes/${row.id}/redemptions/`);
      setRedemptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast('error', 'Error', firstError(err?.response?.data) || 'Failed to load redemptions');
    } finally {
      setRedemptionsLoading(false);
    }
  };

  // Every use of the code (the table shows the latest 500), as a CSV download.
  const exportRedemptions = async () => {
    if (!redemptionsFor || exporting) return;
    try {
      setExporting(true);
      const res = await CustomAxios.get(`special-offer-codes/${redemptionsFor.id}/redemptions/`, {
        params: { export: 'csv' },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${redemptionsFor.code}-redemptions.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast('error', 'Error', 'Could not export redemptions');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Toast ref={toast} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5">Special Offer Codes</Typography>
          <Typography variant="body2" color="text.secondary">
            Codes new users enter on Create Account to get bonus wallet points at sign-up.
          </Typography>
        </Box>
        <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          New Offer Code
        </GrowthPrimaryButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Code or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS_CHIPS).map(([value, s]) => (
              <MenuItem key={value} value={value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? <GrowthPageLoader label="Loading offer codes…" /> : null}
      {!loading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Bonus points</TableCell>
                <TableCell>Valid period</TableCell>
                <TableCell>Used / limit</TableCell>
                <TableCell>Hubs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((row) => {
                const chip = STATUS_CHIPS[row.status] || STATUS_CHIPS.inactive;
                const pct = row.usage_limit ? Math.min(100, (row.used_count / row.usage_limit) * 100) : 0;
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, letterSpacing: 0.6, fontSize: 14 }}>{row.code}</Typography>
                      {row.name ? (
                        <Typography variant="caption" color="text.secondary">{row.name}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.bonus_points}</TableCell>
                    <TableCell>{periodLabel(row)}</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      {row.used_count} / {row.usage_limit != null ? row.usage_limit : 'Unlimited'}
                      {row.usage_limit != null ? (
                        <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.5, height: 6, borderRadius: 3 }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{hubsLabel(row)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(row.is_active)}
                        onChange={() => toggleActive(row)}
                        inputProps={{ 'aria-label': `Enable ${row.code}` }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton aria-label="Edit code" onClick={() => openEdit(row)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View redemptions">
                        <IconButton aria-label="View redemptions" onClick={() => openRedemptions(row)}>
                          <ListAltIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {codes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No offer codes yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editRow ? 'Edit Offer Code' : 'New Offer Code'}
        subtitle="Customers enter this code on Create Account or the Wallet Bonus screen to get bonus wallet points."
        busy={saving}
        busyLabel={editRow ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              {editRow ? 'Save' : 'Create code'}
            </GrowthPrimaryButton>
          </>
        }
      >
        <GrowthFormGrid>
          <GrowthFormSection title="Code & reward" />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Offer code"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
              helperText="4–20 letters or digits. Must be unique."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Display name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              helperText="Internal label; also shown in wallet history."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Bonus wallet points"
              required
              type="number"
              inputProps={{ min: 1 }}
              value={form.bonus_points}
              onChange={(e) => setForm({ ...form, bonus_points: e.target.value })}
              helperText="Credited per use: after OTP on sign-up, or right away on Wallet Bonus."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Points expire after (days)"
              type="number"
              inputProps={{ min: 0 }}
              value={form.expiry_days}
              onChange={(e) => setForm({ ...form, expiry_days: e.target.value })}
              helperText="Blank = standard bonus expiry. 0 = never expires."
            />
          </Grid>

          <GrowthFormSection title="Validity period" />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Valid from"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.valid_from}
              onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              helperText="Blank = valid immediately."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Valid until"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.valid_until}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              helperText="Blank = no end date."
            />
          </Grid>

          <GrowthFormSection title="Usage limits & scope" />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Total redemptions limit"
              type="number"
              inputProps={{ min: 1 }}
              value={form.usage_limit}
              onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
              helperText={`Blank = unlimited. Used so far: ${editRow?.used_count ?? 0}.`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Per-user limit"
              type="number"
              inputProps={{ min: 1 }}
              value={form.per_user_limit}
              onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })}
              helperText="Times one customer can use this code. Blank = unlimited."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="Who can use it"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              helperText="New sign-ups use it on Create Account; existing customers on the Wallet Bonus screen."
            >
              {AUDIENCE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={hubOptions}
              getOptionLabel={(option) => option.name}
              value={hubOptions.filter((h) => form.inventory_ids.includes(Number(h.value)))}
              onChange={(_, newValue) => setForm({ ...form, inventory_ids: newValue.map((h) => Number(h.value)) })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Hubs (optional)"
                  placeholder="Leave empty for all hubs"
                  helperText="Empty = valid in every hub."
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option.name}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip key={option.value} label={option.name} size="small" {...getTagProps({ index })} />
                ))
              }
              isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
              limitTags={3}
            />
          </Grid>
          <Grid item xs={12}>
            <GrowthFormSwitch
              label="Enabled"
              helperText="Turn off to stop new redemptions right away."
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
          </Grid>
        </GrowthFormGrid>
      </GrowthModal>

      <GrowthModal
        open={Boolean(redemptionsFor)}
        onClose={() => setRedemptionsFor(null)}
        title={`Redemptions · ${redemptionsFor?.code || ''}`}
        subtitle="Most recent first (up to 500)."
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setRedemptionsFor(null)}>Close</GrowthSecondaryButton>
            <GrowthPrimaryButton
              onClick={exportRedemptions}
              disabled={exporting || redemptionsLoading || redemptions.length === 0}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </GrowthPrimaryButton>
          </>
        }
      >
        {redemptionsLoading ? (
          <GrowthPageLoader label="Loading redemptions…" />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Hub</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Redeemed on</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptions.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.user_name}</TableCell>
                    <TableCell>{r.mobileno}</TableCell>
                    <TableCell>{r.hub || '—'}</TableCell>
                    <TableCell>{r.bonus_points}</TableCell>
                    <TableCell>{new Date(r.created_on).toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                ))}
                {redemptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No redemptions yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GrowthModal>
    </Box>
  );
}
